/**
 * GraphQL Task Query Hooks
 *
 * Hooks for fetching tasks: list, single, summary.
 * Feature: 078-user-tasks
 */

import { useQuery, gql } from '@apollo/client';
import { Task, TaskStatus, TaskActionType, TaskTargetType } from './useTaskMutationsGraphQL';

// ============================================================================
// GraphQL Queries
// ============================================================================

const TASKS_QUERY = gql`
  query Tasks(
    $filters: TaskFilters
    $sortOrder: TaskSortOrder
    $limit: Int
    $cursor: String
  ) {
    tasks(
      filters: $filters
      sortOrder: $sortOrder
      limit: $limit
      cursor: $cursor
    ) {
      tasks {
        id
        globalId
        actionType
        status
        targetType
        targetId
        targetUrl
        targetContext
        description
        progressCurrent
        progressRequired
        sourceType
        sourceId
        createdAt
        updatedAt
        completedAt
        hasProgress
        progressPercent
      }
      totalCount
      hasMore
      cursor
    }
  }
`;

const TASK_QUERY = gql`
  query Task($id: ID!) {
    task(id: $id) {
      id
      globalId
      actionType
      status
      targetType
      targetId
      targetUrl
      targetContext
      description
      progressCurrent
      progressRequired
      sourceType
      sourceId
      createdAt
      updatedAt
      completedAt
      hasProgress
      progressPercent
    }
  }
`;

const TASK_SUMMARY_QUERY = gql`
  query TaskSummary {
    taskSummary {
      pendingCount
      completedTodayCount
      dismissedCount
    }
  }
`;

const HAS_PENDING_TASKS_FOR_TARGET_QUERY = gql`
  query HasPendingTasksForTarget($targetType: TaskTargetType!, $targetId: ID!) {
    hasPendingTasksForTarget(targetType: $targetType, targetId: $targetId)
  }
`;

// ============================================================================
// Types
// ============================================================================

export interface TaskFilters {
  status?: TaskStatus[];
  actionType?: TaskActionType[];
  targetType?: TaskTargetType[];
  sourceType?: string[];
  createdAfter?: Date;
  createdBefore?: Date;
}

export type TaskSortOrder = 'NEWEST_FIRST' | 'OLDEST_FIRST';

export interface TaskConnection {
  tasks: Task[];
  totalCount: number;
  hasMore: boolean;
  cursor?: string;
}

export interface TaskSummary {
  pendingCount: number;
  completedTodayCount: number;
  dismissedCount: number;
}

// ============================================================================
// Hooks
// ============================================================================

/**
 * Hook for fetching user's tasks with filters and pagination
 */
export function useTasksGraphQL(options?: {
  filters?: TaskFilters;
  sortOrder?: TaskSortOrder;
  limit?: number;
  cursor?: string;
  skip?: boolean;
}) {
  const { data, loading, error, refetch, fetchMore } = useQuery<
    { tasks: TaskConnection },
    {
      filters?: TaskFilters;
      sortOrder?: TaskSortOrder;
      limit?: number;
      cursor?: string;
    }
  >(TASKS_QUERY, {
    variables: {
      filters: options?.filters,
      sortOrder: options?.sortOrder ?? 'NEWEST_FIRST',
      limit: options?.limit ?? 50,
      cursor: options?.cursor,
    },
    skip: options?.skip,
    fetchPolicy: 'cache-and-network',
  });

  const loadMore = async () => {
    if (!data?.tasks.hasMore || !data?.tasks.cursor) {
      return;
    }

    await fetchMore({
      variables: {
        cursor: data.tasks.cursor,
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prev;

        return {
          tasks: {
            ...fetchMoreResult.tasks,
            tasks: [...prev.tasks.tasks, ...fetchMoreResult.tasks.tasks],
          },
        };
      },
    });
  };

  return {
    tasks: data?.tasks.tasks ?? [],
    totalCount: data?.tasks.totalCount ?? 0,
    hasMore: data?.tasks.hasMore ?? false,
    loading,
    error,
    refetch,
    loadMore,
  };
}

/**
 * Hook for fetching a single task by ID
 */
export function useTaskGraphQL(taskId: string | null) {
  const { data, loading, error, refetch } = useQuery<
    { task: Task | null },
    { id: string }
  >(TASK_QUERY, {
    // CRITICAL: Always provide a placeholder ID when skipping to prevent Apollo Client
    // from validating required variables before checking skip.
    variables: { id: taskId || 'skip-placeholder' },
    skip: !taskId,
    fetchPolicy: 'cache-and-network',
  });

  return {
    task: data?.task ?? null,
    loading,
    error,
    refetch,
  };
}

/**
 * Hook for fetching task summary counts
 */
export function useTaskSummaryGraphQL(skip?: boolean) {
  const { data, loading, error, refetch } = useQuery<
    { taskSummary: TaskSummary }
  >(TASK_SUMMARY_QUERY, {
    skip,
    fetchPolicy: 'cache-and-network',
    pollInterval: 60000, // Poll every minute for updates
  });

  return {
    summary: data?.taskSummary ?? {
      pendingCount: 0,
      completedTodayCount: 0,
      dismissedCount: 0,
    },
    loading,
    error,
    refetch,
  };
}

/**
 * Hook for checking if there are pending tasks for a target
 */
export function useHasPendingTasksForTargetGraphQL(
  targetType: TaskTargetType | null,
  targetId: string | null
) {
  const shouldSkip = !targetType || !targetId;
  const { data, loading, error, refetch } = useQuery<
    { hasPendingTasksForTarget: boolean },
    { targetType: TaskTargetType; targetId: string }
  >(HAS_PENDING_TASKS_FOR_TARGET_QUERY, {
    // CRITICAL: Always provide placeholder values when skipping to prevent Apollo Client
    // from validating required variables before checking skip.
    variables: {
      targetType: targetType || ('CRITIQUE' as TaskTargetType),
      targetId: targetId || 'skip-placeholder'
    },
    skip: shouldSkip,
    fetchPolicy: 'cache-and-network',
  });

  return {
    hasPendingTasks: data?.hasPendingTasksForTarget ?? false,
    loading,
    error,
    refetch,
  };
}
